import { logger } from '../lib/logger';
import React from 'react';

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

// Top-level render-error catcher. Production stack traces are minified
// (e.g. "index-XXXX.js:8:48841"), which is useless without the matching
// sourcemap loaded — this renders the React-provided component stack
// (which names actual component names, not minified internals) directly on
// screen so it can be copied straight out of a bug report, and also logs
// both to the console for anyone with devtools open.
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    logger.error('[ErrorBoundary] Render error caught:', error);
    logger.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  override render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div style={{
          padding: '24px', fontFamily: 'monospace', fontSize: '13px',
          background: '#1a1210', color: '#ffdcdc', minHeight: '100vh',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          <h1 style={{ color: '#ff6b6b', fontSize: '16px', marginBottom: '12px' }}>
            ⚠️ Something went wrong rendering the app
          </h1>
          <div style={{ marginBottom: '16px' }}>
            <strong>Error:</strong> {this.state.error.message}
          </div>
          {this.state.error.stack && (
            <details open style={{ marginBottom: '16px' }}>
              <summary style={{ cursor: 'pointer', color: '#ffb3b3' }}>Error stack</summary>
              <pre>{this.state.error.stack}</pre>
            </details>
          )}
          {this.state.componentStack && (
            <details open>
              <summary style={{ cursor: 'pointer', color: '#ffb3b3' }}>Component stack (which component crashed)</summary>
              <pre>{this.state.componentStack}</pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
